import React from 'react';
import { VStack, Flex, Text, Button } from '@chakra-ui/react';
import { INFLUENCER_CANCEL_CUTOFF_HOURS, INFLUENCER_RESCHEDULE_CUTOFF_HOURS, MAX_INFLUENCER_RESCHEDULES } from '@foodfluence/shared';
import { stepTitleProps, eyebrowProps, wizardTokens } from '../styles';
import VisitSummaryCard from './VisitSummaryCard';

interface StepReviewProps {
  /** "Review and book" on accept, "Confirm your new time" on a reschedule. */
  title?: string;
  brandName: string;
  brandLogoUrl?: string;
  locationLabel: string;
  timeLabel: string;
  partySizeLabel: string;
  postByLabel?: string;
  /** Title of the due row ("Draft due by" / "Post by" on go-live collabs). */
  postByTitle?: string;
  turnaroundLabel?: string;
  remainingReschedulesText?: string | null;
  showCreditNote?: boolean;
  /** In-app credit only (isInAppCredit): whole-dollar amount shown in the redeem note. */
  inAppCreditLabel?: string;
  /** accept = first booking; self = creator reschedule (uses one); brand = brand asked (uses none). */
  mode?: 'accept' | 'self' | 'brand';
  onEditLocation?: () => void;
  onEditTime?: () => void;
  onEditPartySize?: () => void;
  onEditTurnaround?: () => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Final review before confirming. Everything the creator chose, read-only; the Confirm CTA lives
// in the wizard's bottom bar.
const StepReview: React.FC<StepReviewProps> = ({
  title = 'Review and book',
  brandName,
  brandLogoUrl,
  locationLabel,
  timeLabel,
  partySizeLabel,
  postByLabel,
  postByTitle,
  turnaroundLabel,
  remainingReschedulesText,
  showCreditNote,
  inAppCreditLabel,
  mode = 'accept',
  onEditLocation,
  onEditTime,
  onEditPartySize,
  onEditTurnaround,
}) => (
  <VStack spacing="18px" align="stretch" fontFamily="display">
    <Text {...stepTitleProps}>{title}</Text>

    <VisitSummaryCard
      brandName={brandName}
      brandLogoUrl={brandLogoUrl}
      locationLabel={locationLabel}
      timeLabel={timeLabel}
      partySizeLabel={partySizeLabel}
      postByLabel={postByLabel}
      postByTitle={postByTitle}
      onEditLocation={onEditLocation}
      onEditTime={onEditTime}
      onEditPartySize={onEditPartySize}
    />

    {turnaroundLabel && (
      <Flex align="center" gap="10px" p="12px 14px" border="1px solid #E4E4E0" borderRadius="18px">
        <Flex flex={1} minW={0} direction="column" gap="3px">
          <Text {...eyebrowProps} lineHeight="normal">Your estimate</Text>
          <Text fontSize="16px" fontWeight={600} lineHeight="normal">{turnaroundLabel}</Text>
          <Text fontSize="13px" color="#55555A" lineHeight="normal">Your hard deadline is the "{postByTitle ?? 'Content due by'}" date above.</Text>
        </Flex>
        {onEditTurnaround && (
          <Button
            variant="unstyled"
            flex="none"
            h="44px"
            minW={0}
            px="4px"
            fontFamily="display"
            fontSize="14px"
            fontWeight={600}
            color={wizardTokens.ink}
            textDecoration="underline"
            aria-label="Edit posting turnaround"
            onClick={onEditTurnaround}
          >
            Edit
          </Button>
        )}
      </Flex>
    )}

    {inAppCreditLabel && (
      <Flex p="14px" borderRadius="16px" bg="#F1FADF" align="center" gap="14px">
        <Text flex={1} fontSize="14px" lineHeight="1.4" color="#2A2A2C">
          Redeem your credit in the app when you arrive.
        </Text>
        <Flex flex="none" direction="column" align="flex-end">
          <Text textStyle="display" fontSize="26px" lineHeight={1.12} textTransform="none">{inAppCreditLabel}</Text>
          <Text fontSize="10px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#3E3E42">Credit</Text>
        </Flex>
      </Flex>
    )}

    {mode === 'self' && remainingReschedulesText && (
      <Text fontSize="13px" lineHeight="1.45" color="#A1260F" fontWeight={600}>{remainingReschedulesText}</Text>
    )}
    {mode === 'brand' && (
      <Text fontSize="13px" lineHeight="1.45" color="#55555A">
        {brandName} asked for a new time, so this change doesn't use your reschedule.
      </Text>
    )}
    {mode === 'accept' && (
      <Text fontSize="13px" lineHeight="1.45" color="#55555A">
        You can reschedule {MAX_INFLUENCER_RESCHEDULES === 1 ? 'once' : plural(MAX_INFLUENCER_RESCHEDULES, 'time')}, up to{' '}
        {INFLUENCER_RESCHEDULE_CUTOFF_HOURS}h before your visit.
        {INFLUENCER_CANCEL_CUTOFF_HOURS === INFLUENCER_RESCHEDULE_CUTOFF_HOURS
          ? ' Cancel locks at the same time.'
          : ` Cancel locks ${INFLUENCER_CANCEL_CUTOFF_HOURS}h before.`}
      </Text>
    )}
    {showCreditNote && (
      <Text fontSize="13px" lineHeight="1.45" color="#55555A">
        The brand handles your credit directly. Follow their credit instructions in the collab details, and message them in your inbox with any questions.
      </Text>
    )}
  </VStack>
);

export default StepReview;
